/**
 * CountBagsModal Component
 *
 * Records the actual bag count for a substrate batch when the cooled trolley
 * is wheeled in. Supports multiple bag sizes per batch (e.g. 4.5kg satrise +
 * 2kg mushroom kit). Once submitted the batch transitions to READY and the
 * counts populate the inoculation FIFO pool.
 */

import { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter
} from '@/components/ui/dialog'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue
} from '@/components/ui/select'
import {
  AlertTriangle,
  Boxes,
  Plus,
  Trash2,
  Loader2
} from 'lucide-react'
import { supabase } from '@/supabase-client'
import { useBlockFormats } from '@/hooks/useSubstrateResources'
import { toast } from 'sonner'

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY

async function callSubstrateBatches(action, payload) {
  const session = await supabase.auth.getSession()
  const accessToken = session?.data?.session?.access_token
  if (!accessToken) throw new Error('Authentication required. Please log in.')

  const response = await fetch(`${SUPABASE_URL}/functions/v1/substrate-batches`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${accessToken}`,
      apikey: SUPABASE_ANON_KEY
    },
    body: JSON.stringify({ action, ...payload })
  })

  const result = await response.json()
  if (!response.ok) {
    throw new Error(result.error || result.message || 'Request failed')
  }
  return result
}

function newRow(blockFormatId = '') {
  return { key: crypto.randomUUID(), block_format_id: blockFormatId, quantity: '' }
}

export default function CountBagsModal({ isOpen, onClose, onSave, batch }) {
  const { blockFormats, loading: formatsLoading } = useBlockFormats()
  const [rows, setRows] = useState(() => [newRow()])
  const [error, setError] = useState(null)
  const [saving, setSaving] = useState(false)

  const activeFormats = useMemo(
    () => blockFormats.filter(f => f.active !== false),
    [blockFormats]
  )

  useEffect(() => {
    if (isOpen) {
      setRows([newRow()])
      setError(null)
      setSaving(false)
    }
  }, [isOpen])

  const totalBags = useMemo(() => {
    return rows.reduce((sum, r) => sum + (parseInt(r.quantity, 10) || 0), 0)
  }, [rows])

  const expectedBlocks = batch?.recipe?.blocks_per_batch || 54
  const variance = totalBags - expectedBlocks
  const variancePercent = expectedBlocks ? (variance / expectedBlocks) * 100 : 0

  const usedFormatIds = useMemo(() => {
    return new Set(rows.map(r => r.block_format_id).filter(Boolean))
  }, [rows])

  const updateRow = (key, patch) => {
    setError(null)
    setRows(prev => prev.map(r => (r.key === key ? { ...r, ...patch } : r)))
  }

  const addRow = () => {
    setRows(prev => [...prev, newRow()])
  }

  const removeRow = (key) => {
    setRows(prev => (prev.length === 1 ? prev : prev.filter(r => r.key !== key)))
  }

  const validate = () => {
    if (rows.length === 0) return 'Add at least one bag size.'
    for (const row of rows) {
      if (!row.block_format_id) return 'Pick a bag size for every row.'
      const qty = parseInt(row.quantity, 10)
      if (!qty || qty < 1) return 'Each row must have a quantity of at least 1.'
    }
    const seen = new Set()
    for (const row of rows) {
      if (seen.has(row.block_format_id)) {
        return 'Each bag size can only appear once. Combine rows of the same size.'
      }
      seen.add(row.block_format_id)
    }
    return null
  }

  const handleSubmit = async () => {
    const validationError = validate()
    if (validationError) {
      setError(validationError)
      return
    }

    setSaving(true)
    setError(null)
    try {
      const fills = rows.map(r => ({
        block_format_id: r.block_format_id,
        quantity: parseInt(r.quantity, 10)
      }))

      const result = await callSubstrateBatches('count-bags', {
        id: batch.id,
        fills
      })

      if (result.data?.warnings?.length) {
        toast.warning(result.data.warnings.join(' '))
      } else {
        toast.success(`Counted ${totalBags} bags for ${batch.batch_code}.`)
      }

      onSave?.(result.data)
      onClose?.()
    } catch (err) {
      setError(err.message || 'Failed to record bag count')
    } finally {
      setSaving(false)
    }
  }

  const varianceLabel = (() => {
    if (totalBags === 0) return null
    if (variance === 0) return { tone: 'neutral', text: `On target (expected ${expectedBlocks})` }
    const sign = variance > 0 ? '+' : ''
    const tone = Math.abs(variancePercent) > 10 ? 'warning' : 'info'
    return {
      tone,
      text: `Expected ${expectedBlocks}, you have ${totalBags} (${sign}${variance}, ${sign}${variancePercent.toFixed(1)}%)`
    }
  })()

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Boxes className="h-5 w-5 text-primary" />
            Count Bags — {batch?.batch_code}
          </DialogTitle>
          <DialogDescription>
            Trolley wheeled in. Enter the bag count for each bag size used in this batch.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {error && (
            <Alert variant="destructive">
              <AlertTriangle className="h-4 w-4" />
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}

          <div className="space-y-2">
            <Label className="text-content-secondary">Bag fills</Label>
            <div className="space-y-2">
              {rows.map((row, idx) => {
                const availableFormats = activeFormats.filter(
                  f => f.id === row.block_format_id || !usedFormatIds.has(f.id)
                )
                return (
                  <div key={row.key} className="flex items-center gap-2">
                    <Select
                      value={row.block_format_id}
                      onValueChange={(value) => updateRow(row.key, { block_format_id: value })}
                      disabled={formatsLoading || saving}
                    >
                      <SelectTrigger className="flex-1">
                        <SelectValue placeholder="Select bag size..." />
                      </SelectTrigger>
                      <SelectContent>
                        {availableFormats.map(f => (
                          <SelectItem key={f.id} value={f.id}>
                            {f.name} ({f.weight_kg}kg)
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      type="number"
                      min="1"
                      placeholder="Qty"
                      className="w-24"
                      value={row.quantity}
                      onChange={(e) => updateRow(row.key, { quantity: e.target.value })}
                      disabled={saving}
                    />
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => removeRow(row.key)}
                      disabled={rows.length === 1 || saving}
                      aria-label={`Remove row ${idx + 1}`}
                    >
                      <Trash2 className="h-4 w-4 text-content-tertiary" />
                    </Button>
                  </div>
                )
              })}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={addRow}
              disabled={saving || activeFormats.length === usedFormatIds.size}
            >
              <Plus className="h-4 w-4 mr-1" />
              Add bag size
            </Button>
          </div>

          <div className="flex items-center justify-between rounded-md border bg-surface-secondary/40 px-3 py-2 text-sm">
            <span className="text-content-secondary">Total bags</span>
            <span className="font-semibold">{totalBags}</span>
          </div>

          {varianceLabel && (
            <div
              className={
                varianceLabel.tone === 'warning'
                  ? 'rounded-md border border-amber-500/30 bg-amber-500/10 px-3 py-2 text-sm text-amber-700'
                  : varianceLabel.tone === 'neutral'
                    ? 'rounded-md border border-emerald-500/30 bg-emerald-500/10 px-3 py-2 text-sm text-emerald-700'
                    : 'rounded-md border border-sky-500/30 bg-sky-500/10 px-3 py-2 text-sm text-sky-700'
              }
            >
              {varianceLabel.text}
            </div>
          )}

          <div className="flex items-center gap-2 text-xs text-content-tertiary">
            <Badge variant="outline">{batch?.recipe?.name || '—'}</Badge>
            <span>Recorded count cannot be edited later — review before saving.</span>
          </div>
        </div>

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={saving || totalBags === 0}>
            {saving ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              `Save count (${totalBags} bags)`
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
